/**
 * timelineGeometry — shared helpers for the 7:00–23:00 editor window.
 * Pure functions, no React deps. Hot-path-safe.
 *
 * Contract with backend: startTime / endTime are "HH:mm" strings (wall clock
 * in the screen's timezone). Everything else in the dashboard speaks
 * minutes-since-midnight internally so the drag math stays simple.
 */

// Editable window: 07:00 (420 min) → 23:00 (1380 min). 16 hours.
// The backend stores a 24h wall clock but admins effectively only schedule
// the on-air day here; pre-7am / post-11pm is rare and can be reached via
// the inspector numeric entry field if ever needed.
export const MIN_MINUTES = 420;
export const MAX_MINUTES = 1380;
export const TOTAL_MINUTES = MAX_MINUTES - MIN_MINUTES;

export const SNAP_MINUTES = 5;
export const MIN_CLIP_MINUTES = 5;

/** "HH:mm" → minutes since midnight; null/invalid → null. */
export function parseHHMM(value) {
  if (typeof value !== 'string') return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const hours = Number(m[1]);
  const mins = Number(m[2]);
  if (hours < 0 || hours > 23 || mins < 0 || mins > 59) return null;
  return hours * 60 + mins;
}

/** minutes → "HH:mm" (zero-padded). Clamped to [0, 1439]. */
export function formatHHMM(minutes) {
  if (!Number.isFinite(minutes)) return '--:--';
  const clamped = Math.max(0, Math.min(1439, Math.round(minutes)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Snap a minute value to the configured grid (5-min by default). */
export function snap(minute, snapTo = SNAP_MINUTES) {
  if (!Number.isFinite(minute)) return MIN_MINUTES;
  return Math.round(minute / snapTo) * snapTo;
}

/** Convert minutes to CSS pixels within a container of width `containerPx`.
 *
 * Accepts an optional windowRange {startMin, endMin} to render against a
 * dynamic window (e.g. Focus 2h, Business hours). Falls back to the default
 * 07:00–23:00 editable window so older call sites keep working.
 */
export function minuteToPx(minute, containerPx, windowRange) {
  if (!containerPx || containerPx <= 0) return 0;
  const start = windowRange?.startMin ?? MIN_MINUTES;
  const end = windowRange?.endMin ?? MAX_MINUTES;
  const span = Math.max(1, end - start);
  return ((minute - start) / span) * containerPx;
}

/** Convert CSS pixels back to minute coordinate. */
export function pxToMinute(px, containerPx, windowRange) {
  const start = windowRange?.startMin ?? MIN_MINUTES;
  const end = windowRange?.endMin ?? MAX_MINUTES;
  if (!containerPx || containerPx <= 0) return start;
  const span = Math.max(1, end - start);
  return start + (px / containerPx) * span;
}


/**
 * Normalize a server intent row into the shape the editor uses internally.
 * Backend responses use camelCase already; this just guards missing fields
 * and coerces types so the renderer never has to null-check.
 */
export function normalizeIntent(row) {
  if (!row || typeof row !== 'object') return null;
  const id = row.intentId || row.id || row._id || null;
  if (!id) return null;
  const startMin = parseHHMM(row.startTime);
  const endMin = parseHHMM(row.endTime);
  if (startMin === null || endMin === null || endMin <= startMin) return null;
  const track = [1, 2, 3].includes(row.track) ? row.track : 1;
  const overlapPolicy = row.overlapPolicy === 'replace' ? 'replace' : 'merge';
  return {
    id,
    intentId: id,
    projectId: row.projectId || null,
    screenId: row.screenId || null,
    name: typeof row.name === 'string' && row.name.trim() ? row.name : 'Untitled clip',
    contentIds: Array.isArray(row.contentIds) ? row.contentIds.slice() : [],
    track,
    startTime: row.startTime,
    endTime: row.endTime,
    startMinutes: startMin,
    endMinutes: endMin,
    startDate: row.startDate || null,
    endDate: row.endDate || null,
    // daysOfWeek dropped 2026-04-23 — a week is a week, no weekday filtering.
    // Field still read off legacy rows for one release; do not write it back.
    daysOfWeek: null,
    overlapPolicy,
    status: row.status || 'active',
    createdAt: row.createdAt || null,
    updatedAt: row.updatedAt || null,
  };
}
